import { useState, useCallback, useMemo } from "react";
import {
  Button,
  Group,
  Modal,
  TextInput,
  Stack,
  Text,
  Divider,
  Title,
} from "@mantine/core";
import { toast } from "sonner";
import { IconLink, IconLock, IconInfoCircle } from "@tabler/icons-react";

import { useLinkAccountingSoftwareMutation } from "../api/queries/organizationQueries";
import { IntegrationConnectButton } from "./integrations/IntegrationButtons";

interface MorningFormData {
  clientId: string;
  clientSecret: string;
}

const INITIAL_FORM_DATA: MorningFormData = {
  clientId: "",
  clientSecret: "",
};

export function MorningConnection() {
  const { mutate: linkAccountingSoftware, isPending: isLinking } =
    useLinkAccountingSoftwareMutation();
  const [opened, setOpened] = useState(false);
  const [formData, setFormData] = useState<MorningFormData>(INITIAL_FORM_DATA);

  const isFormValid = useMemo(
    () =>
      formData.clientId.trim() !== "" && formData.clientSecret.trim() !== "",
    [formData],
  );

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpened(false);
    resetForm();
  }, [resetForm]);

  const handleFormChange = useCallback(
    (field: keyof MorningFormData) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      },
    [],
  );

  const handleConnect = useCallback(() => {
    if (!isFormValid) {
      toast.error("Please enter your Morning client ID and secret");
      return;
    }

    const clientId = formData.clientId.trim();
    const clientSecret = formData.clientSecret.trim();

    linkAccountingSoftware(
      {
        name: "Morning",
        client_id: clientId,
        client_secret: clientSecret,
      },
      {
        onSuccess: () => {
          toast.success("Successfully connected to Morning");
          resetForm();
          handleCloseModal();
        },
        onError: (err: any) => {
          resetForm();
          const detail = err?.response?.data?.detail;
          const errorMessage =
            (typeof detail === "object" && detail?.message
              ? detail.message
              : typeof detail === "string"
                ? detail
                : null) ||
            err?.message ||
            "Failed to save Morning credentials";
          toast.error("Failed to connect to Morning", {
            description: errorMessage,
          });
        },
      },
    );
  }, [
    formData,
    isFormValid,
    linkAccountingSoftware,
    handleCloseModal,
    resetForm,
  ]);

  return (
    <>
      <IntegrationConnectButton onClick={() => setOpened(true)} />

      <Modal
        opened={opened}
        onClose={handleCloseModal}
        title={
          <Group gap="xs">
            <IconLink size={20} />
            <Title order={4}>Connect to Morning</Title>
          </Group>
        }
        centered
        closeOnClickOutside={!isLinking}
        closeOnEscape={!isLinking}
        size="lg"
        radius="md"
        padding="xl"
      >
        <Stack gap="lg">
          <Text size="sm" c="dimmed" style={{ lineHeight: 1.6 }}>
            Enter the same Morning client ID and secret used in the Green
            Invoice API (the id and secret from Apps Script). They are
            encrypted and stored per organization, then used to sync invoices
            and customers.
          </Text>

          <Divider />

          <Stack gap="md">
            <TextInput
              label="Client ID"
              placeholder="Morning client ID"
              value={formData.clientId}
              onChange={handleFormChange("clientId")}
              required
              disabled={isLinking}
              leftSection={<IconLock size={18} />}
              radius="md"
              size="md"
            />
            <TextInput
              label="Client secret"
              placeholder="Morning client secret"
              value={formData.clientSecret}
              onChange={handleFormChange("clientSecret")}
              type="password"
              required
              disabled={isLinking}
              leftSection={<IconLock size={18} />}
              radius="md"
              size="md"
            />
          </Stack>

          <Group
            p="md"
            style={{
              backgroundColor: "var(--mantine-color-blue-0)",
              borderRadius: "var(--mantine-radius-md)",
              border: "1px solid var(--mantine-color-blue-2)",
            }}
            gap="xs"
          >
            <IconInfoCircle size={18} color="var(--mantine-color-blue-6)" />
            <Text size="xs" c="dimmed" style={{ flex: 1 }}>
              Credentials are encrypted (AES-256-GCM) and never stored in
              plaintext. Datamint tests the token endpoint before saving.
            </Text>
          </Group>

          <Divider />

          <Group justify="flex-end" gap="sm" mt="xs">
            <Button
              variant="outline"
              onClick={handleCloseModal}
              disabled={isLinking}
              radius="md"
              size="md"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConnect}
              loading={isLinking}
              disabled={!isFormValid || isLinking}
              leftSection={!isLinking && <IconLink size={16} />}
              radius="md"
              size="md"
            >
              {isLinking ? "Connecting..." : "Connect"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
